import { type ChangeEvent, type FormEvent, memo, useCallback, useState } from 'react';

import Sort from '@/components/Sort';
import { DIRECTION } from '@/types/sort';
import InputField from '@/ui/components/Form/InputField';
import type { OptionType, defaultOptionTypes } from '@/ui/components/Form/Select/types';

import type { Props } from './types';

export const myPeopleDefaultSearchParams = {
  direction: DIRECTION.DESC,
  ordering: 'created_at',
  page: '1',
  search: ''
};

const myPeopleSortOptions = [
  { label: 'First Name', value: 'first_name' },
  { label: 'Last Name', value: 'last_name' },
  { label: 'App open date', value: 'last_app_open_at' },
  { label: 'Creation date', value: 'created_at' }
];

const MyPeopleFilterForm = ({ activeParams, setParams }: Props) => {
  const [search, setSearch] = useState<string>(activeParams.search);

  const handleSortChange = useCallback(
    (option: OptionType<defaultOptionTypes>) => {
      if (option.value)
        setParams((currentParams) => {
          if (option.value !== myPeopleDefaultSearchParams.ordering)
            currentParams.set('ordering', option.value as string);
          else currentParams.delete('ordering');
          currentParams.delete('page');
          return currentParams;
        });
    },
    [setParams]
  );

  const handleSortDirectionChange = useCallback(() => {
    setParams((currentParams) => {
      const [nonDefaultDirection] = Object.values(DIRECTION).filter(
        (direction) => direction !== myPeopleDefaultSearchParams.direction
      );
      if (activeParams.direction === myPeopleDefaultSearchParams.direction)
        currentParams.set('direction', nonDefaultDirection);
      else currentParams.delete('direction');
      currentParams.delete('page');
      return currentParams;
    });
  }, [activeParams.direction, setParams]);

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value),
    []
  );

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    setParams((currentParams) => {
      if (search) currentParams.set('search', search);
      else currentParams.delete('search');
      return currentParams;
    });
  };

  return (
    <form className="relative" onSubmit={handleSearchSubmit}>
      <div className=" border-gray-200 pt-1 relative">
        <div className="flex items-center justify-between max-w-7xl space-x-2 divide-x divide-gray-200 text-sm">
          <div className="mb-1 grow">
            <InputField
              id="search"
              placeholder="Search"
              type="text"
              typeHelper="search"
              value={search}
              onChange={handleSearchChange}
            />
          </div>

          <div className="pl-1">
            <Sort
              sortOptions={myPeopleSortOptions}
              ascending={activeParams.direction === DIRECTION.ASC}
              value={activeParams.ordering}
              onChange={handleSortChange}
              onDirectionChange={handleSortDirectionChange}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default memo(MyPeopleFilterForm);
